import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { OpenStoreCalendarContainer, OpenStoreCalendarForm } from 'components/Calendar'
import { Spacer } from 'components/Utility'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getModalEdited } from 'reducks/modal/selectors'
import { OrangeButton, TextButton } from 'components/Button'
import { useSelect } from 'hooks/form'
import { useSelectStoreCalendar } from 'hooks/storeCalendar'
import { closeModal, editContentInModal } from 'reducks/modal/operations'
import { StoreCalendarType } from 'types/storeCalendar'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { saveStoreCalendarsConfirm } from 'reducks/confirm/operations'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { FlexColumn } from 'components/Flex'
import isSameDay from 'date-fns/isSameDay'
import { dateToString, parseToDate } from 'lib/helper'
import {
  getHolidays,
  getLastOrderTime,
  getLastPickupTime,
  getOpenTime,
  getOrderStartTime,
} from 'reducks/store/selectors'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { BusinessTimeType, OpenTimeType } from 'types/store'
import { getBusinessTimeOnDay, getOpenTimeOnDay } from 'lib/businessHour'
const daysOfWeek = new DaysOfWeek()

type Props = {
  defaultDate?: Date
}

const OpenCalendarModal: FC<Props> = (props) => {
  const { defaultDate } = props
  const dispatch = useDispatch()

  const edited = useSelector((state: State) => getModalEdited(state))
  const currentStoreCalendars = useSelector((state: State) => getStoreCalendars(state))
  const currentOpenTime: OpenTimeType = useSelector((state: State) => getOpenTime(state))
  const currentOrderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const currentLastPickupTime: BusinessTimeType = useSelector((state: State) => getLastPickupTime(state))
  const currentLastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))

  const [activeDate, setActiveDate] = useState<Date | undefined>(defaultDate)
  const [storeCalendars, setStoreCalendars] = useState<StoreCalendarType[]>(currentStoreCalendars)
  const handleStoreCalendars = useSelectStoreCalendar(setStoreCalendars)
  const selectDate = useCallback((date: Date | undefined) => setActiveDate(date), [setActiveDate])
  const {
    state: orderStartTime,
    handleState: selectOrderStartTime,
    setState: setOrderStartTime,
  } = useSelect<string>('')
  const { state: openTime, handleState: selectOpenTime, setState: setOpenTime } = useSelect<string>('')
  const { state: lastOrderTime, handleState: selectLastOrderTime, setState: setLastOrderTime } = useSelect<string>('')
  const {
    state: lastPickupTime,
    handleState: selectLastPickupTime,
    setState: setLastPickupTime,
  } = useSelect<string>('')
  const {
    state: defaultDayOfWeek,
    handleState: selectDefaultDayOfWeek,
    setState: setDefaultDayOfWeek,
  } = useSelect<DayOfWeekType | ''>('')

  const businessDays = useMemo(
    () => daysOfWeek.getDaysOfWeek().filter((dayOfWeek) => !holidays.includes(dayOfWeek)),
    [holidays]
  )

  useEffect(() => {
    if (!activeDate) {
      setOrderStartTime('')
      setOpenTime('')
      setLastOrderTime('')
      setLastPickupTime('')
      setDefaultDayOfWeek('')
    } else {
      const dayOfWeek = daysOfWeek.getDayOfWeek(activeDate, specialHolidays)
      const isHoliday = holidays.some((holiday: DayOfWeekType) => holiday === dayOfWeek)
      const calendar = storeCalendars.find((storeCalendar) => {
        return storeCalendar.status === 'open' && isSameDay(parseToDate(storeCalendar.date), activeDate)
      })
      // 臨時営業日か定休日であれば、既存の値を設定
      if (calendar || isHoliday) {
        setOrderStartTime(calendar?.order_start_time || '')
        setOpenTime(calendar?.open_time || '')
        setLastOrderTime(calendar?.last_order_time || '')
        setLastPickupTime(calendar?.last_pickup_time || '')
        setDefaultDayOfWeek(calendar?.default_day_of_week || '')
      } else {
        // 通常営業日を臨時営業日にする場合は、通常営業日の値を初期値として設定
        setOrderStartTime(getBusinessTimeOnDay(dayOfWeek, currentOrderStartTime) ?? '')
        setOpenTime(getOpenTimeOnDay(dayOfWeek, currentOpenTime) ?? '')
        setLastOrderTime(getBusinessTimeOnDay(dayOfWeek, currentLastOrderTime) ?? '')
        setLastPickupTime(getBusinessTimeOnDay(dayOfWeek, currentLastPickupTime) ?? '')
        setDefaultDayOfWeek(dayOfWeek)
      }
    }
  }, [activeDate])

  // 営業時間と引き継ぎ曜日を通常営業日と異なる値に変更した場合、
  useEffect(() => {
    if (
      !activeDate ||
      orderStartTime === '' ||
      openTime === '' ||
      lastOrderTime === '' ||
      lastPickupTime === '' ||
      defaultDayOfWeek === ''
    )
      return
    // 既存の値を設定したときは追加しない
    const dayOfWeek = daysOfWeek.getDayOfWeek(activeDate, specialHolidays)
    if (
      getBusinessTimeOnDay(dayOfWeek, currentOrderStartTime) === orderStartTime &&
      getOpenTimeOnDay(dayOfWeek, currentOpenTime) === openTime &&
      getBusinessTimeOnDay(dayOfWeek, currentLastOrderTime) === lastOrderTime &&
      getBusinessTimeOnDay(dayOfWeek, currentLastPickupTime) === lastPickupTime &&
      dayOfWeek === defaultDayOfWeek
    ) {
      return
    }
    handleStoreCalendars({
      date: dateToString(activeDate),
      default_day_of_week: defaultDayOfWeek,
      open_time: openTime,
      order_start_time: orderStartTime,
      last_pickup_time: lastPickupTime,
      last_order_time: lastOrderTime,
      status: 'open',
    })
    dispatch(editContentInModal())
  }, [orderStartTime, openTime, lastOrderTime, lastPickupTime, defaultDayOfWeek])

  const isHoliday = useMemo(() => {
    if (!activeDate) return false
    return holidays.some((holiday: DayOfWeekType) => {
      return holiday === daysOfWeek.getDayOfWeek(activeDate, specialHolidays)
    })
  }, [activeDate, holidays])

  const revertToNormal = useCallback(() => {
    setActiveDate((prevState) => {
      if (!prevState) return undefined
      handleStoreCalendars({
        date: dateToString(prevState),
        status: '',
      })
      return undefined
    })
  }, [setActiveDate, handleStoreCalendars])

  return (
    <div>
      <OpenStoreCalendarContainer activeDate={activeDate} storeCalendars={storeCalendars} selectDate={selectDate} />
      <Spacer size={16} />
      {activeDate && (
        <OpenStoreCalendarForm
          orderStartTime={orderStartTime}
          openTime={openTime}
          lastOrderTime={lastOrderTime}
          lastPickupTime={lastPickupTime}
          selectOrderStartTime={selectOrderStartTime}
          selectOpenTime={selectOpenTime}
          selectLastOrderTime={selectLastOrderTime}
          selectLastPickupTime={selectLastPickupTime}
          businessDays={businessDays}
          defaultDayOfWeek={defaultDayOfWeek}
          selectDefaultDayOfWeek={selectDefaultDayOfWeek}
        />
      )}
      <Spacer size={16} />
      <OrangeButton
        disabled={
          !edited ||
          (activeDate && (orderStartTime === '' || openTime === '' || lastOrderTime === '' || lastPickupTime === ''))
        }
        label={'保存して終了する'}
        onClick={() => {
          dispatch(saveStoreCalendarsConfirm(storeCalendars, 'open'))
          dispatch(closeModal())
        }}
        size={'medium'}
        type={'main'}
      />
      {typeof activeDate !== 'undefined' && (
        <FlexColumn alignItems={'center'}>
          <Spacer size={24} />
          <TextButton label={isHoliday ? '定休日に戻す' : '通常営業日に戻す'} onClick={revertToNormal} />
        </FlexColumn>
      )}
    </div>
  )
}

export default OpenCalendarModal
